/**
 * The review of an extractor draft (F4 of the import hub on /app/twin/data-sources), in the order a reviewer decides
 * by: what changed since the draft before (and the note it was sent back with), what it will import (the checks, the
 * counts, each stream's equipment with where its choice came from, its first rows, and where each column of the file
 * goes), what it skips, its author's questions, the defect words it proposes, and its code.
 *
 * Views are pure (rendered in tests without providers): the review sheet in import-hub.tsx holds the hooks, and
 * lib/imports/review reads the draft's JSON and words what it finds. The one exception is the form a word's select
 * opens for a new defect type of the org's own (new-defect-type.tsx), mounted only once a reviewer chooses it: it adds
 * the type itself and chooses it for the word, which the sheet then saves with the approval.
 */
import type { ReactNode } from "react";
import { Loader2, TriangleAlert } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import type { ImportExtractorVersion } from "@/lib/api/imports";
import type { SourceRole } from "@/lib/api/twin-import";
import type { VocabBinding, VocabDefectTarget } from "@/lib/api/twin-vocabulary";
import {
  bindGroups,
  missingBindings,
  noEquipmentText,
  streamEquipmentKeys,
  type Bindings,
  type ManifestStream,
} from "@/lib/imports/hub";
import { footerOf, type ReviewPhase } from "@/lib/imports/review-flow";
import { asksRole, roleHint, roleOptions, type RoleChoice } from "@/lib/imports/source-roles";
import {
  CATALOG_HREF,
  bindNote,
  equipmentProposalsOf,
  own,
  type BindNote,
  type EquipmentProposal,
  type Prefill,
} from "@/lib/imports/proposal";
import { nothingChangedText, type SinceChanges, type SinceHeader } from "@/lib/imports/since-draft";
import type { AnswerChoice, Answers, GivenAnswer, ResponseLine, SendBackState } from "@/lib/imports/send-back";
import { AnswerControls, GivenAnswersView, ReviewResponseView } from "./import-review-answers";
import { NewDefectTypeForReview } from "./new-defect-type";
import {
  NEW_TYPE,
  NEW_TYPE_OPTION,
  WORD_CHOICES,
  defectChoice,
  assumptionsOf,
  cellText,
  decisionText,
  spellingText,
  twinText,
  coverageOf,
  defectOptions,
  defectTargetsOf,
  draftSummary,
  fmtCount,
  plural,
  proposalText,
  proposedWordsOf,
  questionGroups,
  reasonTally,
  sampleColumns,
  selftestOf,
  selftestText,
  streamLine,
  summaryFacts,
  wordBindings,
  wordRows,
  wordsPlanText,
  type Coverage,
  type DefectTargets,
  type DraftQuestion,
  type DraftSummary,
  type QuestionGroup,
  type SelftestSummary,
  type StreamSummary,
  type WordChoice,
  type WordRow,
} from "@/lib/imports/review";

const SELECT_CLS =
  "w-full rounded-md border border-border bg-surface px-2 py-1.5 text-xs text-on-surface focus:border-brand focus:outline-none disabled:opacity-60";
const TH = "whitespace-nowrap px-2 py-1 text-left font-medium text-on-surface-muted";
const TD = "px-2 py-1 align-top";
const H4 = "text-xs font-medium text-on-surface";

export interface EntityOption {
  id: string;
  name: string;
  /** The item's kind (EQUIPMENT, INSTRUMENT, STATION, TRANSPORT): the picker groups by it. */
  kind?: string;
}

/**
 * The reviewer's role for each stream's data source (one series per port, S1b and S5): what its file is, history (the
 * machine's own recorded log) or report (a sheet made for people). A stream not chosen for is left as its reader says.
 */
export interface ReviewRoles {
  choices: Readonly<Record<string, RoleChoice>>;
  /** The role each stream's data source holds now, where the stream already writes through one. */
  current?: Readonly<Record<string, SourceRole | null>>;
  onChoose: (streamKey: string, choice: RoleChoice) => void;
}

/** The reviewer's defect words: the list to choose from, the org's decisions so far, and the choices made here. */
export interface ReviewWords {
  /** The defects a binding may name; null while not known (still loading, or an older server). */
  targets: readonly VocabDefectTarget[] | null;
  /** The org's bindings, so a word it already decided starts from that decision. */
  bindings?: readonly VocabBinding[] | null;
  /** The reviewer's choice by word key; a word not here keeps where it started. */
  choices: Readonly<Record<string, WordChoice>>;
  onChoose: (key: string, choice: WordChoice) => void;
  /** Said above the words, e.g. that the reviewer's role may not save them. */
  hint?: string;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">{title}</h3>
      {children}
    </section>
  );
}

function ChecksBlock({ version, selftest }: { version: ImportExtractorVersion; selftest: SelftestSummary }) {
  const v = version.validation;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-on-surface-muted">Dry run on its file:</span>
        {v?.ok === true && <Badge variant="success">passed</Badge>}
        {v?.ok === false && <Badge variant="danger">failed</Badge>}
        {v === undefined || v === null ? (
          <span className="text-on-surface-muted">not dry-run: registered by hand, not written for a file</span>
        ) : null}
        <span className="text-on-surface-muted">· Self-test:</span>
        <span className={selftest.ok === false ? "text-danger" : "text-on-surface"}>{selftestText(selftest)}</span>
      </div>
      {v?.ok === false && (
        <ul className="list-disc pl-5 text-xs text-danger">
          {(v.errors ?? []).map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FactsBlock({ summary }: { summary: DraftSummary }) {
  if (!summary.from) {
    return (
      <p className="text-xs text-on-surface-muted">
        No counts: neither the dry run nor the self-test got as far as reading the file.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {summaryFacts(summary).map((f) => (
          <div key={f.label} className="rounded-md border border-border px-2 py-1.5">
            <dt className="text-[10px] uppercase tracking-wide text-on-surface-muted">{f.label}</dt>
            <dd className="text-sm font-medium text-on-surface">{f.value}</dd>
          </div>
        ))}
      </dl>
      <div className="text-[11px] text-on-surface-muted">
        Counted by the {summary.from === "dry run" ? "dry run on its file" : "self-test of the draft"}.
      </div>
    </div>
  );
}

function PreviewTable({ stream, s }: { stream: string; s: StreamSummary }) {
  const cols = sampleColumns(s.sample);
  const of = s.rows !== null && s.rows > s.sample.length ? ` of ${fmtCount(s.rows)}` : "";
  return (
    <div className="flex flex-col gap-1">
      <div className="text-[11px] text-on-surface-muted">
        The first {plural(s.sample.length, "row")}
        {of}, as they import:
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[11px]" aria-label={`The first rows of ${stream}`}>
          <thead className="border-b border-border">
            <tr>
              {cols.map((c) => (
                <th key={c} scope="col" className={`${TH} font-mono`}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.sample.map((row, i) => (
              <tr key={i} className="border-b border-border last:border-b-0">
                {cols.map((c) => (
                  <td key={c} className={`${TD} whitespace-nowrap font-mono text-on-surface`}>
                    {cellText(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const NOTE_CLS: Record<BindNote["tone"], string> = {
  info: "text-info",
  warning: "text-warning",
  muted: "text-on-surface-muted",
};

/** What a picker says under it: where its value came from and why, or that no item fits and where to add one. */
function BindNoteView({ note }: { note: BindNote }) {
  return (
    <span className={`text-[11px] ${NOTE_CLS[note.tone]}`}>
      {note.text}
      {note.addFromCatalog && (
        <>
          {" "}
          {/* The same tab: a new one would not carry the sign-in (it lives in the tab's session storage). */}
          <a href={CATALOG_HREF} className="underline">
            Open the process graph (leaves this review)
          </a>
        </>
      )}
    </span>
  );
}

function StreamCard({
  s,
  stream,
  entities,
  chosen,
  sources,
  picked,
  proposals,
  itemsReady,
  onBind,
  roles,
  locked,
}: {
  s: StreamSummary;
  stream: ManifestStream | undefined;
  entities: EntityOption[];
  chosen: Bindings;
  sources: Prefill["source"];
  picked: Bindings;
  proposals: Readonly<Record<string, EquipmentProposal>>;
  /** The plant's items are loaded: until then no note is said, since an empty list proves nothing. */
  itemsReady: boolean;
  onBind: (streamKey: string, equipmentKey: string, entityId: string) => void;
  roles?: ReviewRoles;
  locked: boolean;
}) {
  const keys = stream ? streamEquipmentKeys(stream) : [];
  // Bound by the manifest's own key, as the approval's body and its check read it (lib/imports/hub); s.key is trimmed.
  const bindKey = stream?.key ?? s.key;
  const line = streamLine(s);
  const groups = bindGroups(entities);
  return (
    <Card className="flex flex-col gap-2 p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs text-on-surface">{s.key}</span>
        {s.mode && (
          <Badge variant="outline" className="text-[10px]">
            {s.mode}
          </Badge>
        )}
        {s.kind && (
          <Badge variant="outline" className="text-[10px]">
            {s.kind}
          </Badge>
        )}
      </div>
      {line && <div className="text-xs text-on-surface">{line}</div>}
      <div className="text-[11px] text-on-surface-muted">
        {s.params.map((p) => `${p.tag}${p.unit ? ` (${p.unit})` : ""}`).join(" · ") || "no parameters"}
      </div>
      {stream &&
        (keys.length === 0 ? (
          <span className="text-[11px] text-on-surface-muted">{noEquipmentText(stream)}</span>
        ) : (
          keys.map((eq) => {
            const value = own(own(chosen, bindKey), eq) ?? "";
            const note = itemsReady
              ? bindNote({
                  value,
                  source: own(sources[bindKey], eq),
                  picked: own(picked[bindKey], eq) !== undefined,
                  proposal: own(proposals, eq),
                  items: entities,
                })
              : null;
            return (
              <label key={eq} className="flex flex-col gap-1 text-xs">
                <span className="text-on-surface-muted">
                  The file calls this <span className="font-mono text-on-surface">{eq || s.key}</span>. Which item of
                  your plant is it?
                </span>
                <select
                  className={SELECT_CLS}
                  value={value}
                  disabled={locked}
                  onChange={(ev) => onBind(bindKey, eq, ev.target.value)}
                  aria-label={`Equipment for ${s.key} ${eq}`}
                >
                  <option value="">Choose the plant item…</option>
                  {groups.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.options.map((en) => (
                        <option key={en.id} value={en.id}>
                          {en.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {note && <BindNoteView note={note} />}
              </label>
            );
          })
        ))}
      {stream && roles && asksRole(stream) && (
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-on-surface-muted">Where this file comes from</span>
          <select
            className={SELECT_CLS}
            value={roles.choices[bindKey] ?? "reader"}
            disabled={locked}
            onChange={(ev) => roles.onChoose(bindKey, ev.target.value as RoleChoice)}
            aria-label={`Role of ${s.key}`}
          >
            {roleOptions(stream).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-on-surface-muted" data-role-hint="">
            {roleHint(stream, roles.current?.[bindKey])}
          </span>
        </label>
      )}
      {s.sample.length > 0 && <PreviewTable stream={s.key} s={s} />}
    </Card>
  );
}

function CoverageBlock({ coverage }: { coverage: Coverage }) {
  if (coverage.rows.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <h4 className={H4}>Where each column goes</h4>
        <p className="text-xs text-on-surface-muted">The draft says nothing of its columns.</p>
      </div>
    );
  }
  const sheets = coverage.rows.some((r) => r.sheet);
  return (
    <div className="flex flex-col gap-1">
      <h4 className={H4}>Where each column goes</h4>
      <div className="text-[11px] text-on-surface-muted">
        {coverage.from === "placements" ? "As its author placed each column." : "As the reader declares its columns."}
        {coverage.flagged > 0 && (
          <span className="text-warning">
            {" "}
            {coverage.flagged === 1 ? "1 column has" : `${fmtCount(coverage.flagged)} columns have`} no destination this
            reader writes.
          </span>
        )}
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs" aria-label="Where each column goes">
          <thead className="border-b border-border">
            <tr>
              {sheets && (
                <th scope="col" className={TH}>
                  Sheet
                </th>
              )}
              <th scope="col" className={TH}>
                Column
              </th>
              <th scope="col" className={TH}>
                Goes to
              </th>
              <th scope="col" className={TH}>
                Why
              </th>
            </tr>
          </thead>
          <tbody>
            {coverage.rows.map((r) => (
              <tr
                key={`${r.sheet}\u0000${r.column}\u0000${r.goesTo}`}
                className={`border-b border-border last:border-b-0 ${r.flag ? "bg-warning/10" : ""}`}
              >
                {sheets && <td className={`${TD} text-on-surface-muted`}>{r.sheet}</td>}
                <td className={`${TD} font-mono text-on-surface`}>{r.column}</td>
                <td className={TD}>
                  {r.flag ? (
                    <span className="inline-flex items-center gap-1 text-warning">
                      <TriangleAlert className="size-3 shrink-0" aria-hidden="true" />
                      {r.goesTo ? `${r.goesTo}: ${r.flag}` : r.flag}
                    </span>
                  ) : (
                    <span className={r.skipped ? "text-on-surface-muted" : "text-on-surface"}>{r.goesTo}</span>
                  )}
                </td>
                <td className={`${TD} text-on-surface-muted`}>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SkipsBlock({ summary, coverage, selftest }: { summary: DraftSummary; coverage: Coverage; selftest: SelftestSummary }) {
  const setAside = summary.setAside ?? 0;
  const sample = summary.quarantineSample;
  const notImported = coverage.rows.filter((r) => r.skipped).length;
  const nothing =
    notImported === 0 &&
    summary.skipped.length === 0 &&
    summary.dropped.length === 0 &&
    setAside === 0 &&
    sample.length === 0 &&
    selftest.warnings.length === 0 &&
    selftest.held.length === 0;
  if (nothing) {
    return (
      <p className="text-xs text-on-surface-muted">
        {summary.from ? "Nothing: every row it read imports." : "Not known: no run got as far as reading the file."}
      </p>
    );
  }
  const tally = reasonTally(sample);
  return (
    <div className="flex flex-col gap-3">
      {notImported > 0 && (
        <p className="text-xs text-on-surface">
          {notImported === 1 ? "1 column is" : `${fmtCount(notImported)} columns are`} not imported (above, with why).
        </p>
      )}
      {summary.skipped.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>Skipped on purpose</h4>
          <ul className="list-disc pl-5 text-xs text-on-surface">
            {summary.skipped.map((k) => (
              <li key={`${k.stream}\u0000${k.reason}`}>
                {plural(k.count, "row")}: {k.reason}
                {k.stream && <span className="text-on-surface-muted"> ({k.stream})</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {summary.dropped.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>Left out by the checks</h4>
          <p className="text-xs text-on-surface">{summary.dropped.join(" · ")}</p>
        </div>
      )}
      {(setAside > 0 || sample.length > 0) && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>Set aside, each with its reason</h4>
          <p className="text-xs text-on-surface">
            {fmtCount(Math.max(setAside, sample.length))} set aside
            {sample.length > 0 && sample.length < setAside ? `; the first ${fmtCount(sample.length)} are below` : ""}.
            {tally.length > 0 && (
              <span className="text-on-surface-muted">
                {" "}
                Among {sample.length < setAside ? "these" : "them"}: {tally.map((t) => `${fmtCount(t.count)} ${t.reason}`).join(", ")}.
              </span>
            )}
          </p>
          {sample.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-[11px]" aria-label="What was set aside">
                <thead className="border-b border-border">
                  <tr>
                    <th scope="col" className={TH}>
                      Stream
                    </th>
                    <th scope="col" className={TH}>
                      Row
                    </th>
                    <th scope="col" className={TH}>
                      Column
                    </th>
                    <th scope="col" className={TH}>
                      Value
                    </th>
                    <th scope="col" className={TH}>
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sample.map((q, i) => (
                    <tr key={i} className="border-b border-border last:border-b-0">
                      <td className={`${TD} font-mono text-on-surface-muted`}>{q.stream}</td>
                      <td className={`${TD} text-on-surface-muted`}>{q.row ?? ""}</td>
                      <td className={`${TD} font-mono text-on-surface`}>{q.column}</td>
                      <td className={`${TD} font-mono text-on-surface`}>{cellText(q.value)}</td>
                      <td className={`${TD} text-on-surface`}>{q.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {selftest.warnings.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>Flagged by its checks</h4>
          <ul className="list-disc pl-5 text-xs text-on-surface">
            {selftest.warnings.slice(0, 12).map((w, i) => (
              <li key={i}>
                {w.rule && <span className="font-medium">{w.rule.replace(/_/g, " ")}</span>}
                {w.where && <span className="font-mono text-on-surface-muted"> {w.where}</span>}
                {(w.rule || w.where) && w.detail ? ": " : ""}
                {w.detail}
              </li>
            ))}
          </ul>
          {selftest.warnings.length > 12 && (
            <div className="text-[11px] text-on-surface-muted">and {fmtCount(selftest.warnings.length - 12)} more</div>
          )}
        </div>
      )}
      {selftest.held.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>Held for review</h4>
          <ul className="list-disc pl-5 text-xs text-warning">
            {selftest.held.map((h, i) => (
              <li key={i}>
                {h.where && <span className="font-mono">{h.where}: </span>}
                {h.detail || h.rule}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The reviewer's answers to the draft's questions while it is reviewed (plan v21 B5). */
export interface ReviewAnswering {
  choices: Answers;
  onChoose: (index: number, choice: AnswerChoice | null) => void;
  locked: boolean;
  /** What each question's choices open with; the review's "If you send it back:" when absent. */
  lead?: string;
}

function QuestionView({ q, answering }: { q: DraftQuestion; answering?: ReviewAnswering }) {
  if (!q.saw && !q.assume && !q.changes) return <span className="text-xs text-on-surface">{q.text}</span>;
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-border p-2 text-xs text-on-surface">
      {q.text && <div className="font-medium">{q.text}</div>}
      {q.saw && (
        <div>
          <span className="text-on-surface-muted">Saw: </span>
          {q.saw}
        </div>
      )}
      {q.assume && (
        <div>
          <span className="text-on-surface-muted">Assumed: </span>
          {q.assume}
        </div>
      )}
      {q.changes && (
        <div>
          <span className="text-on-surface-muted">If that is wrong: </span>
          {q.changes}
        </div>
      )}
      {answering && q.index !== undefined && (
        <AnswerControls
          index={q.index}
          about={q.saw || q.text}
          choice={answering.choices[q.index]}
          onChoose={answering.onChoose}
          locked={answering.locked}
          lead={answering.lead}
        />
      )}
    </div>
  );
}

const PRIORITY_TONE: Record<string, string> = { blocks_import: "text-danger", blocks_analysis: "text-warning" };

/** The draft's questions by priority, and what it assumed; with `answering`, each four-part question takes an answer
 *  (the review's send-back, and the request for a revision of the version in use). */
export function QuestionsBlock({
  groups,
  assumptions,
  answering,
}: {
  groups: QuestionGroup[];
  assumptions: string[];
  answering?: ReviewAnswering;
}) {
  if (groups.length === 0 && assumptions.length === 0) {
    return <p className="text-xs text-on-surface-muted">Its author asks nothing, and states no assumption.</p>;
  }
  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <div key={g.title} className="flex flex-col gap-1.5">
          <h4 className={`text-xs font-medium ${PRIORITY_TONE[g.priority ?? ""] ?? "text-on-surface"}`}>
            {g.title} ({fmtCount(g.questions.length)})
            {g.hint && <span className="font-normal text-on-surface-muted">: {g.hint}</span>}
          </h4>
          <ul className="flex flex-col gap-1.5">
            {g.questions.map((q, i) => (
              <li key={q.index ?? `t${i}`}>
                <QuestionView q={q} answering={answering} />
              </li>
            ))}
          </ul>
        </div>
      ))}
      {assumptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className={H4}>It assumed</h4>
          <ul className="list-disc pl-5 text-xs text-on-surface">
            {assumptions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function WordsBlock({
  version,
  rows,
  targets,
  words,
}: {
  version: ImportExtractorVersion;
  rows: WordRow[];
  targets: DefectTargets | null;
  words: ReviewWords | undefined;
}) {
  if (rows.length === 0) return <p className="text-xs text-on-surface-muted">The draft proposes no defect words.</p>;
  const choices: Readonly<Record<string, WordChoice>> = words?.choices ?? {};
  const saved = wordBindings(rows, choices, version).length;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-on-surface-muted">
        The plant&apos;s own words for its defects, as this file books them, and what each means to the analysis. Your
        choices are saved as the org&apos;s words when you approve; a word left for later is not saved, and stays the
        plant&apos;s own category.
      </p>
      {words?.hint && <p className="text-xs text-warning">{words.hint}</p>}
      {!targets && (
        <p className="text-[11px] text-on-surface-muted">
          The list of defects is not loaded: each proposal stays as written, and the server checks it when it is saved.
        </p>
      )}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs" aria-label="Defect words">
          <thead className="border-b border-border">
            <tr>
              <th scope="col" className={TH}>
                The plant&apos;s word
              </th>
              <th scope="col" className={TH}>
                Means
              </th>
              <th scope="col" className={TH}>
                From its author
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const current = choices[r.key] ?? r.initial;
              const defects = defectOptions(r, targets, current);
              return (
                <tr key={r.key} className="border-b border-border last:border-b-0">
                  <td className={`${TD} font-medium text-on-surface`}>{r.word}</td>
                  <td className={`${TD} min-w-48`}>
                    <select
                      className={SELECT_CLS}
                      value={current}
                      disabled={!words}
                      onChange={(ev) => words?.onChoose(r.key, ev.target.value)}
                      aria-label={`What ${r.word} means`}
                    >
                      {WORD_CHOICES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                      {defects.length > 0 && (
                        <optgroup label="A defect">
                          {defects.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {words && <option value={NEW_TYPE_OPTION.value}>{NEW_TYPE_OPTION.label}</option>}
                    </select>
                    {words && current === NEW_TYPE && (
                      <NewDefectTypeForReview
                        word={r.word}
                        onChosen={(key) => words.onChoose(r.key, defectChoice(key))}
                        onCancel={() => words.onChoose(r.key, r.initial)}
                      />
                    )}
                  </td>
                  <td className={`${TD} text-[11px] text-on-surface-muted`}>
                    <div className={r.proposalUnknown ? "text-warning" : undefined}>{proposalText(r)}</div>
                    {r.decided && <div className="text-info">{decisionText(r, current)}</div>}
                    {spellingText(r) && <div className="text-info">{spellingText(r)}</div>}
                    {twinText(r, rows, choices) && <div className="text-warning">{twinText(r, rows, choices)}</div>}
                    {r.note && <div>{r.note}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-on-surface">{wordsPlanText(rows, choices, saved)}</div>
    </div>
  );
}

/** The version before, as the review compares with it: its head at once (from the extractor's list of versions), and
 *  what changed once it is loaded (null until then; `failed` when the version before could not be loaded). */
export interface SinceView {
  header: SinceHeader;
  changes: SinceChanges | null;
  failed?: boolean;
  /** The answers given when the version before was sent back (plan v21 B5). */
  given?: GivenAnswer[];
  /** What this draft says it did with that send-back (its review_response). */
  response?: ResponseLine[];
}

/** What changed since the version before, under the note it was sent back with. */
function SinceBlock({ since }: { since: SinceView }) {
  const { header, changes } = since;
  return (
    <Section title={`Since v${header.previous} (${header.previousState})`}>
      {header.sentBackWith && (
        <div className="rounded-md border border-border p-2 text-xs text-on-surface">
          <span className="text-on-surface-muted">Sent back with: </span>
          {header.sentBackWith}
        </div>
      )}
      {since.given && <GivenAnswersView answers={since.given} />}
      {since.response && <ReviewResponseView lines={since.response} />}
      {changes === null ? (
        <div className="text-xs text-on-surface-muted">
          {since.failed ? `Couldn't load v${header.previous} to compare with.` : `Comparing with v${header.previous}…`}
        </div>
      ) : (
        <>
          {changes.changes.length === 0 ? (
            <div className={`text-xs ${header.sentBackWith ? "text-warning" : "text-on-surface-muted"}`}>
              {nothingChangedText(header.previous, changes)}
              {header.sentBackWith && " If the note asked for something else, check the source below."}
            </div>
          ) : (
            <ul className="list-disc pl-5 text-xs text-on-surface">
              {changes.changes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          )}
          {changes.countsNote && <div className="text-xs text-on-surface-muted">{changes.countsNote}</div>}
        </>
      )}
    </Section>
  );
}

const ITEMS_TEXT = {
  loading: "Loading your plant's items…",
  failed: "Couldn't load your plant's items, so the equipment can't be chosen here.",
} as const;

/**
 * Everything a reviewer reads in one version, and the equipment and defect words they choose. `locked` once the
 * version is approved: its equipment is bound, and only its words can still be saved.
 */
export function VersionReviewView({
  version,
  entities,
  chosen,
  sources = {},
  picked = {},
  since = null,
  items = "ready",
  answering,
  onBind,
  roles,
  words,
  locked = false,
}: {
  version: ImportExtractorVersion;
  entities: EntityOption[];
  chosen: Bindings;
  /** Where each picker's starting value came from (lib/imports/proposal prefillBindings): each one says so, and why. */
  sources?: Prefill["source"];
  /** The reviewer's own choices: a picker they set says only where the brain's proposal differs. */
  picked?: Bindings;
  /** What changed since the version before (lib/imports/since-draft), when there is one. */
  since?: SinceView | null;
  /** Whether the plant's items (`entities`) are loaded: the pickers' notes wait for them. */
  items?: "loading" | "ready" | "failed";
  /** The reviewer's answers to the draft's questions, for a send-back (plan v21 B5); none, no controls. */
  answering?: ReviewAnswering;
  onBind: (streamKey: string, equipmentKey: string, entityId: string) => void;
  /** The role of each stream's data source, asked of a stream that lands on equipment; none: not asked. */
  roles?: ReviewRoles;
  words?: ReviewWords;
  locked?: boolean;
}) {
  const streams = (version.manifest?.streams ?? []) as ManifestStream[];
  const missing = missingBindings(streams, chosen);
  const summary = draftSummary(version);
  const coverage = coverageOf(version);
  const selftest = selftestOf(version);
  const targets = defectTargetsOf(words?.targets);
  const rows = wordRows(proposedWordsOf(version.author_notes), targets, words?.bindings);
  const proposals = equipmentProposalsOf(version.author_notes);
  const lines = version.source ? version.source.trimEnd().split("\n").length : 0;
  return (
    <div className="flex flex-col gap-6 p-5 text-sm">
      <div className="text-xs text-on-surface-muted">
        v{version.version} · written against forge_extract {version.helper_version} · source{" "}
        <span className="font-mono">{version.source_sha256.slice(0, 12)}</span>
      </div>

      {since && <SinceBlock since={since} />}

      <Section title="What it will import">
        <ChecksBlock version={version} selftest={selftest} />
        <FactsBlock summary={summary} />
        {summary.streams.map((s) => (
          <StreamCard
            key={s.key}
            s={s}
            stream={streams.find((m) => (typeof m.key === "string" ? m.key.trim() : "") === s.key)}
            entities={entities}
            chosen={chosen}
            sources={sources}
            picked={picked}
            proposals={proposals}
            itemsReady={items === "ready"}
            onBind={onBind}
            roles={roles}
            locked={locked}
          />
        ))}
        {items !== "ready" && <div className="text-xs text-on-surface-muted">{ITEMS_TEXT[items]}</div>}
        {items === "ready" && missing.length > 0 && (
          <div className="text-xs text-warning">still to bind: {missing.join(", ")}</div>
        )}
        <CoverageBlock coverage={coverage} />
      </Section>

      <Section title="What it skips">
        <SkipsBlock summary={summary} coverage={coverage} selftest={selftest} />
      </Section>

      <Section title="Questions from its author">
        <QuestionsBlock
          groups={questionGroups(version.author_notes)}
          assumptions={assumptionsOf(version.author_notes)}
          answering={answering}
        />
      </Section>

      <Section title="Defect words">
        <WordsBlock version={version} rows={rows} targets={targets} words={words} />
      </Section>

      {version.source && (
        <details className="rounded-md border border-border">
          <summary className="cursor-pointer px-3 py-2 text-xs text-on-surface-muted">The source ({lines} lines)</summary>
          <pre className="max-h-96 overflow-auto p-3 font-mono text-[11px] leading-relaxed text-on-surface">
            {version.source}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * The version is approved and its words were not saved: both said plainly, in the sheet's footer, with a way to save
 * the words again. Nothing withdraws the approval.
 */
export function WordsNotSavedNotice({
  text,
  retrying,
  onRetry,
  onClose,
}: {
  text: string;
  retrying: boolean;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-md border border-danger/50 bg-danger/10 p-3 text-xs text-on-surface">
      <div className="flex items-start gap-2">
        <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-danger" aria-hidden="true" />
        <span>{text}</span>
      </div>
      <div className="text-on-surface-muted">
        Change a word above if the server refused it, then save the words again. Or close: the reader stays approved,
        and its words are not saved.
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
        <Button size="sm" disabled={retrying} aria-label="Save the words again" onClick={onRetry}>
          {retrying && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Save the words again
        </Button>
      </div>
    </div>
  );
}

/**
 * The review sheet's footer in each phase (lib/imports/review-flow): the note to send a draft back, Send back and
 * Approve and bind, both off while anything runs; once approved and the words failed, the notice that says so and
 * offers them again.
 */
export function ReviewFooterView({
  phase,
  missing,
  waiting,
  note,
  sendBack,
  onNote,
  onApprove,
  onReject,
  onRetry,
  onClose,
  refusal,
}: {
  phase: ReviewPhase;
  /** Equipment keys still to bind. */
  missing: number;
  /** Approve waits for what the review reads first (the org's decided words). */
  waiting: boolean;
  note: string;
  /** Whether it can be sent back, from the note and the answers (lib/imports/send-back sendBackState); none: the note. */
  sendBack?: SendBackState;
  onNote: (note: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onRetry: () => void;
  onClose: () => void;
  /** Why the last approval was refused, where the reviewer can fix it here (a role conflict) or send it again. */
  refusal?: string | null;
}) {
  if (phase.kind === "words_failed" || phase.kind === "retrying") {
    return (
      <WordsNotSavedNotice text={phase.message} retrying={phase.kind === "retrying"} onRetry={onRetry} onClose={onClose} />
    );
  }
  const f = footerOf(phase, { missing, note, waiting, canSendBack: sendBack?.ok });
  return (
    <>
      {refusal && (
        <div role="alert" className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-2 text-xs text-on-surface">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>{refusal}</span>
        </div>
      )}
      <textarea
        className={`${SELECT_CLS} min-h-16`}
        placeholder="To send it back: say what to change, and answer its questions above (the author reads both)"
        value={note}
        onChange={(e) => onNote(e.target.value)}
        aria-label="Note for the author"
      />
      <div className="flex items-center justify-end gap-2">
        {sendBack && !sendBack.ok && sendBack.blankCorrection && (
          <span className="text-[11px] text-warning">{sendBack.reason}</span>
        )}
        <Button variant="outline" size="sm" disabled={f.rejectDisabled} aria-label="Send back" onClick={onReject}>
          Send back
        </Button>
        <Button size="sm" disabled={f.approveDisabled} aria-label="Approve and bind" onClick={onApprove}>
          {f.busy && <Loader2 className="mr-1.5 size-3.5 animate-spin" />}
          Approve and bind
        </Button>
      </div>
    </>
  );
}
